import { api_fetch } from "./client"

export interface FileImport {
  id: string
  org_id: string
  s3_url: string
  file_import_mapping_id?: string
  headers?: string[]
  /** Backend-detected format: generic, bc_sot_detail, blue_planner_html */
  import_format?: string
  created_at: string
  updated_at: string
}

export type TransactionKind = "invoice" | "prepayment" | "repayment" | "deduction" | "correction"

export interface FileImportRow {
  id: string
  org_id: string
  file_import_id: string
  date?: string
  invoice_number?: string
  debit_amount?: number
  credit_amount?: number
  net_amount?: number
  memo?: string
  check_number?: string
  source?: string
  row_data: string[]
  deduction_id?: string
  suggested_deduction_id?: string
  kind?: TransactionKind
  split?: Split[]
  display_splits: DisplaySplit[]
  split_source: SplitSource
  matched_invoice_number?: string
  /** Name of the promotion the matched deduction is linked to, if any. */
  matched_promotion_name?: string
  /** Id of the promotion the matched deduction is linked to, if any. */
  matched_promotion_id?: string
  /** Suggested split from auto-coding rules (not yet saved) */
  suggested_split?: DisplaySplit[]
  /** BC SoT: rows with the same number become one deduction on import (1-based). */
  bc_sot_import_group?: number
  /** BC SoT: how many file rows share this import group. */
  bc_sot_import_group_rows?: number
  created_at: string
  updated_at: string
}

export interface Split {
  account: string
  amount: number
  description?: string
}

export interface DisplaySplit {
  account: string
  amount: number
  description?: string
}

export type SplitSource = "row" | "deduction" | "not_split"

export async function listFileImports(): Promise<FileImport[]> {
  const res = await api_fetch<FileImport[]>("/file-imports")
  if (res.ok) {
    return res.value.data
  }
  throw res.error
}

export async function uploadFileImport(file: File): Promise<FileImport> {
  const formData = new FormData()
  formData.append("file", file)

  const res = await fetch("/api/file-imports/upload", {
    method: "POST",
    body: formData,
    credentials: "include",
  })

  if (!res.ok) {
    throw new Error("Failed to upload file")
  }

  return res.json() as Promise<FileImport>
}

export async function getFileImport(id: string): Promise<FileImport> {
  const res = await api_fetch<FileImport>(`/file-imports/${id}`)
  if (res.ok) {
    return res.value.data
  }
  throw res.error
}

export async function getFileImportRows(id: string): Promise<FileImportRow[]> {
  const res = await api_fetch<FileImportRow[]>(`/file-imports/${id}/rows`)
  if (res.ok) {
    return res.value.data
  }
  throw res.error
}

export interface UpdateFileImportRowPayload {
  splits?: Split[]
  invoice_number?: string
  deduction_id?: string
  kind?: TransactionKind | null
}

export async function updateFileImportRow(
  rowId: string,
  payload: UpdateFileImportRowPayload
): Promise<FileImportRow> {
  const res = await api_fetch<FileImportRow>(`/file-import-rows/${rowId}`, {
    body: payload,
  })
  if (res.ok) {
    return res.value.data
  }
  throw res.error
}

export function getFileImportExportUrl(id: string): string {
  return `/api/file-imports/${id}/export`
}

export function getFileImportDownloadUrl(s3_url: string): string {
  return `/api/_s3?s3_uri=${encodeURIComponent(s3_url)}`
}

export interface DeductionSearchResult {
  id: string
  invoice_number: string
  invoice_amount: number
  source: string
  kind: string
}

export async function searchDeductions(query: string): Promise<DeductionSearchResult[]> {
  if (!query) return []
  const res = await api_fetch<DeductionSearchResult[]>("/deduction", { params: { q: query } })
  if (res.ok) {
    return res.value.data
  }
  throw res.error
}

export interface ImportDeductionsPayload {
  row_ids?: string[]
}

export type ImportSkipReason =
  | "already_linked"
  | "missing_invoice_number"
  | "missing_kind"
  | "missing_source"
  | "bc_group_missing_source"
  | "bc_group_missing_kind"

export type ImportRowOutcome =
  | { kind: "created_deduction"; deduction_id: string }
  | { kind: "linked_existing_deduction"; deduction_id: string }
  | { kind: "skipped"; reason: ImportSkipReason }

export interface FileImportRowOutcome {
  row_id: string
  invoice_hint?: string
  outcome: ImportRowOutcome
}

export interface ImportDeductionsResponse {
  imported_count: number
  skipped_count: number
  /** File import rows linked to a deduction (new or existing). */
  rows_linked_count: number
  /** Omitted only against older servers; treat as []. */
  row_outcomes?: FileImportRowOutcome[]
}

export async function importDeductions(
  fileImportId: string,
  payload: ImportDeductionsPayload
): Promise<ImportDeductionsResponse> {
  const res = await api_fetch<ImportDeductionsResponse>(`/file-imports/${fileImportId}/import-deductions`, {
    body: payload,
  })
  if (res.ok) {
    return res.value.data
  }
  throw res.error
}
